/**
 * useSSOCallback Hook
 * Hook React para detectar y procesar el callback del SSO
 * Principio: Separation of Concerns - Lógica de React separada del handler
 */

import { useEffect, useRef } from 'react';
import { processCallback, hasCallbackInUrl } from '../services/sso/SSOCallbackHandler';
import { setUserCookie } from '../context/cookieUtils';
import { markSSOAttempted, SSOConfig } from '../services/sso/SSOConfig';

/**
 * Hook para procesar el callback del SSO cuando el usuario regresa
 * @param {Object} options - Opciones del hook
 * @param {Function} options.onSuccess - Callback cuando el SSO callback es exitoso
 * @param {Function} options.onError - Callback cuando el SSO callback falla
 * @param {Function} options.setUser - Función para establecer el usuario en el estado
 * @param {boolean} options.enabled - Si el hook está habilitado (default: true)
 */
export default function useSSOCallback({
  onSuccess,
  onError,
  setUser,
  enabled = true,
} = {}) {
  const processedRef = useRef(false);

  useEffect(() => {
    // Solo procesar una vez
    if (!enabled || processedRef.current) {
      return;
    }

    // Verificar si hay callback en la URL
    if (!hasCallbackInUrl()) {
      return;
    }

    processedRef.current = true;

    console.log('[useSSOCallback] 🔄 SSO callback detected in URL');

    try {
      // Procesar el callback
      const result = processCallback();

      if (!result.success) {
        console.error('[useSSOCallback] ❌ Callback processing failed:', result.message);
        
        // Marcar como fallido
        markSSOAttempted(SSOConfig.STATUS.BACKEND_ERROR);

        // Callback de error
        if (onError && typeof onError === 'function') {
          onError(result);
        }
        
        return;
      }

      console.log('[useSSOCallback] ✅ Callback successful, setting up user...');

      // Construir objeto de usuario compatible con el sistema
      const userData = {
        // Datos del usuario
        id: result.user.id,
        username: result.user.username,
        email: result.user.email,
        name: result.user.name || result.user.username,
        
        // Tokens
        access: result.accessToken,
        refresh: result.refreshToken,
        
        // Permisos
        is_staff: result.user.is_staff || false,
        is_superuser: result.user.is_superuser || false,
        is_active: result.user.is_active !== false,
        
        // Organización
        organization_id: result.user.organization_id?.id || null,
        organization: result.user.organization_id || null,
        
        // Role
        role_id: result.user.role_id || null,
        
        // Metadata
        provider: 'sso',
        timestamp: new Date().toISOString(),
        date_joined: result.user.date_joined,
        
        // Campos adicionales del usuario
        ...result.user,
      };

      // Guardar en cookie
      try {
        setUserCookie(userData);
        console.log('[useSSOCallback] 💾 User data saved to cookie');
      } catch (cookieError) {
        console.warn('[useSSOCallback] ⚠️ Error saving cookie:', cookieError);
      }

      // Establecer usuario en el estado de la app
      if (setUser && typeof setUser === 'function') {
        setUser(userData);
        console.log('[useSSOCallback] 👤 User set in application state');
      }

      // Marcar SSO como exitoso
      markSSOAttempted(SSOConfig.STATUS.SUCCESS);

      // Callback de éxito
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(userData, result);
      }

      console.log('[useSSOCallback] 🎉 SSO callback completed successfully!');

    } catch (error) {
      console.error('[useSSOCallback] ❌ Unexpected error processing callback:', error);
      
      markSSOAttempted(SSOConfig.STATUS.BACKEND_ERROR);

      if (onError && typeof onError === 'function') {
        onError({
          success: false,
          message: 'Unexpected error processing SSO callback',
          error,
        });
      }
    }
  }, [enabled, onSuccess, onError, setUser]);

  return null;
}
